import { elemBind, elemUnbind } from '../../../../../../utilities/elem.js';
import { cachedDetect } from '../../../../../../utilities/detect.js';
import type { Detect } from '../../../../../../types/player-api-types.ts';
import { doTimeout, clearTimeouts } from '../../../../../../utilities/timeout-utils.js';
import { count } from '../../../../../../utilities/metrics.js';
import { wlog } from '../../../../../../utilities/wlog.js';
import { sequentialBufferedRange } from './buffering.js';

type PrefixedLogger = { notice: (...messages: unknown[]) => void };

const logger = (
  wlog as unknown as { getPrefixedFunctions: (prefix: string) => PrefixedLogger }
).getPrefixedFunctions('SimpleVideo');

// WebKit 27 + MSE (hls.js on ManagedMediaSource) intermittently wedges at the
// start of playback: play() resolves and media is buffered, but currentTime
// never advances and no event fires (endless spinner). Seen right after hls.js
// skips our streams' small head gap, more often with several MSE players busy.
// It's the start-of-playback twin of webkit_loop_hack.js, with the same fix:
// seek to the buffered start and play.
//
// For the first START_WINDOW seconds we poll (the stall can swallow every event)
// and recover when the playhead or a seek hasn't moved with MIN_BUFFER_AHEAD
// buffered. We set currentTime directly: simpleVideo.seek() hides and pauses
// the video if `playing` never fired, and remaps the time through cuts.
//
// The seek goes NUDGE_BACK behind the playhead, clamped to the buffered start.
// For the stall we've seen (parked at 0.133s, buffer from 0.083s) that's the
// buffered start, which is what's been verified to unpark it. Later in the
// window it only replays a moment, and stays under the player's public `seek`
// event threshold, so stats don't record a rewind.

const START_WINDOW = 3;
const STUCK_THRESHOLD_MS = 1500;
const MIN_BUFFER_AHEAD = 1;
const MAX_ATTEMPTS = 2;
const MIN_WEBKIT_MAJOR = 27;
// how far past the seek target the playhead must get to count as resumed.
const SUCCESS_MARGIN = 0.25;
const NUDGE_BACK = 0.1;

type StartHackHandlers = Record<string, () => void>;

type SimpleVideoLike = {
  attributes: { hashedId?: string; pageUrl?: string };
  state?: { destroyed?: boolean };
  uuid: string;
  video: HTMLVideoElement;
};

const boundHandlers = new WeakMap<SimpleVideoLike, StartHackHandlers>();

// detect's iOS and browser versions are useless here: Safari freezes its UA at
// "OS 18_x" and "AppleWebKit/605.1.15". Safari (and iOS Edge) send Version/N;
// iOS Chrome and in-app browsers send the real iOS version instead.
export const webkitMajorVersion = (userAgent: string): number | null => {
  const match =
    /Version\/(\d+)/.exec(userAgent) ?? /(?:iPhone|iPad|iPod).* OS (\d+)_/.exec(userAgent);
  return match ? Number(match[1]) : null;
};

export const timeoutKeyFor = (simpleVideo: SimpleVideoLike): string =>
  `${simpleVideo.uuid}.webkit_start_recovery`;

// MSE plays a blob: URL; native HLS (never seen stalling) plays a real one.
const isPlayingFromMse = (video: HTMLVideoElement): boolean => {
  return typeof video.currentSrc === 'string' && video.currentSrc.startsWith('blob:');
};

const isInStartWindow = (video: HTMLVideoElement): boolean => {
  return !video.paused && !video.ended && video.currentTime < START_WINDOW;
};

const isGone = (simpleVideo: SimpleVideoLike): boolean => Boolean(simpleVideo.state?.destroyed);

export const setup = (simpleVideo: SimpleVideoLike): void => {
  const webkitMajor = webkitMajorVersion(navigator.userAgent);
  if (
    !(cachedDetect() as Detect).actualWebkit ||
    webkitMajor === null ||
    webkitMajor < MIN_WEBKIT_MAJOR
  ) {
    return;
  }

  const timeoutKey = timeoutKeyFor(simpleVideo);
  let attempts = 0;
  // the last recovery's seek target, until playback is seen moving past it.
  let pendingSuccess: { attempt: number; target: number } | null = null;

  const disarm = (): void => {
    clearTimeouts(timeoutKey);
  };

  let arm: () => void;

  const recoverIfStuck = (armedAtTime: number, armedWhileSeeking: boolean): void => {
    const { video } = simpleVideo;
    if (isGone(simpleVideo) || !isInStartWindow(video)) {
      return;
    }
    if (!isPlayingFromMse(video)) {
      // no source yet: keep watching.
      if (video.currentSrc === '') {
        arm();
      }
      return;
    }

    if (document.visibilityState !== 'visible') {
      // background tabs can freeze without pausing; judge only once visible.
      arm();
      return;
    }

    const moved = video.currentTime !== armedAtTime;
    // a seek that started since arming gets one more period.
    const seekStillYoung = video.seeking && !armedWhileSeeking;
    if (moved || seekStillYoung) {
      arm();
      return;
    }

    const range = sequentialBufferedRange(simpleVideo) as [number, number] | null;
    const bufferedAhead = range ? range[1] - Math.max(video.currentTime, range[0]) : 0;
    if (!range || bufferedAhead < MIN_BUFFER_AHEAD) {
      // genuinely buffering (hls.js's job), or the end of a short loop (the loop hack's).
      arm();
      return;
    }
    const target = Math.max(range[0], video.currentTime - NUDGE_BACK);

    attempts += 1;
    count('player/webkit-start-recovery', 1, {
      attempt: attempts,
      buffered_ahead: bufferedAhead,
      hashed_id: simpleVideo.attributes.hashedId,
      ready_state: video.readyState,
      stalled_at: video.currentTime,
      url: simpleVideo.attributes.pageUrl ?? window.location.href,
      webkit_major: webkitMajor,
    });
    pendingSuccess = { attempt: attempts, target };
    // plain text: wlog's saved lines (first1000LogLines) flatten objects.
    logger.notice(
      `webkit start stall: recovering, attempt ${attempts} at ${video.currentTime.toFixed(3)}s ` +
        `-> ${target.toFixed(3)}s, ${bufferedAhead.toFixed(2)}s buffered ahead, readyState ${video.readyState}`,
    );

    video.currentTime = target;
    // if `seeked` never fires, the next check sees the unfinished seek and retries.
    arm();
    // already unpaused; this just nudges WebKit, as the loop hack does.
    video.play().catch((error: unknown) => {
      logger.notice('webkit start stall: play after recovery failed', error);
    });
  };

  arm = (): void => {
    const { video } = simpleVideo;
    if (attempts >= MAX_ATTEMPTS || !isInStartWindow(video)) {
      disarm();
      return;
    }
    const armedAtTime = video.currentTime;
    const armedWhileSeeking = video.seeking;
    doTimeout(timeoutKey, () => recoverIfStuck(armedAtTime, armedWhileSeeking), STUCK_THRESHOLD_MS);
  };

  const onTimeUpdate = (): void => {
    if (pendingSuccess && simpleVideo.video.currentTime > pendingSuccess.target + SUCCESS_MARGIN) {
      count('player/webkit-start-recovery-success', 1, {
        attempt: pendingSuccess.attempt,
        hashed_id: simpleVideo.attributes.hashedId,
        url: simpleVideo.attributes.pageUrl ?? window.location.href,
        webkit_major: webkitMajor,
      });
      pendingSuccess = null;
    }
    arm();
  };

  const onEmptied = (): void => {
    attempts = 0;
    pendingSuccess = null;
    disarm();
  };

  const handlers: StartHackHandlers = {
    emptied: onEmptied,
    ended: disarm,
    pause: disarm,
    play: () => arm(),
    playing: () => arm(),
    seeked: () => arm(),
    timeupdate: onTimeUpdate,
  };
  boundHandlers.set(simpleVideo, handlers);
  Object.entries(handlers).forEach(([event, handler]) => {
    elemBind(simpleVideo.video, event, handler);
  });
};

export const teardown = (simpleVideo: SimpleVideoLike): void => {
  const handlers = boundHandlers.get(simpleVideo);
  if (!handlers) {
    return;
  }
  Object.entries(handlers).forEach(([event, handler]) => {
    elemUnbind(simpleVideo.video, event, handler);
  });
  clearTimeouts(timeoutKeyFor(simpleVideo));
  boundHandlers.delete(simpleVideo);
};
